import { useMemo, useRef, useState } from "react";

import {
  Alert,
  FormControl,
  Link,
  MenuItem,
  Select,
  Snackbar,
  Stack,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";
import { Link as RouterLink } from "react-router-dom";

import { useRoleContext, useWriterComponentState } from "hooks";

import { PREFERENCE_LABELS } from "./constants";
import { ClientAppEvent, EXPAND_LANES_QUERY_PARAM } from "../../../constants";

import type { SelectChangeEvent } from "@mui/material/Select";

interface NotificationPreferenceSelectProps {
  shipperRoleId: number;
  initialPreference: ShipmentNotificationPreference;
}

export function NotificationPreferenceSelect(
  props: NotificationPreferenceSelectProps,
) {
  const { shipperRoleId, initialPreference } = props;
  const { role } = useRoleContext();
  const posthog = usePostHog();

  const [preference, setPreference] =
    useState<ShipmentNotificationPreference>(initialPreference);

  const previousPreferenceRef =
    useRef<ShipmentNotificationPreference>(preference);

  const writeConfig = useMemo(
    () => ({
      method: "patch" as const,
      url: `/carriers/${role?.id}/followed_shippers/${shipperRoleId}/notification-preference`,
    }),
    [role?.id, shipperRoleId],
  );

  const { loading, error, success, write, resetState } =
    useWriterComponentState({
      errorMessage: "Failed to update notification preference",
      writeConfig,
      onWriteFailure: () => {
        setPreference(previousPreferenceRef.current);
      },
    });

  const handleChange = (event: SelectChangeEvent) => {
    const newPreference = event.target.value as ShipmentNotificationPreference;
    previousPreferenceRef.current = preference;
    setPreference(newPreference);

    write(
      () => {
        posthog?.capture(
          ClientAppEvent.CARRIER_SHIPMENT_NOTIFICATION_PREFERENCE_UPDATED,
          {
            shipper_role_id: shipperRoleId,
            previous_preference: previousPreferenceRef.current,
            new_preference: newPreference,
          },
        );
      },
      { carrier_notification_preference: newPreference },
    );
  };

  return (
    <>
      <Stack>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select value={preference} onChange={handleChange} disabled={loading}>
            {Object.entries(PREFERENCE_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {preference === "LANE_FILTERED" && (
          <Link
            component={RouterLink}
            to={`/?${EXPAND_LANES_QUERY_PARAM}=true`}
            variant="caption"
          >
            Manage lanes →
          </Link>
        )}
      </Stack>
      <Snackbar
        open={!!error}
        onClose={() => resetState()}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => resetState()}>
          Failed to update notification preference
        </Alert>
      </Snackbar>
      <Snackbar
        open={success}
        onClose={() => resetState()}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => resetState()}>
          Notification preference updated
        </Alert>
      </Snackbar>
    </>
  );
}
